/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import api from "../axiosConfig";
import { esAdministrador } from "../utils/perfil";

const TermsContext = createContext();

export const useTerms = () => useContext(TermsContext);

export const TermsProvider = ({ children }) => {
  const [tycAceptados, setTycAceptados] = useState(null); // null = no verificado
  const [politicaPrivacidadAceptada, setPoliticaPrivacidadAceptada] = useState(null);
  const [verificando, setVerificando] = useState(true);
  const [mostrarModalTerminos, setMostrarModalTerminos] = useState(false);
  const [mostrarModalPolitica, setMostrarModalPolitica] = useState(false);

  const TYC_VERSION = "1.0.0"; // Versión actual de los términos y condiciones

  // Verificar aceptaciones al cargar
  useEffect(() => {
    const verificarAceptaciones = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        // Términos y política son del conductor (se guardan en su registro)
        if (!token || esAdministrador()) {
          setVerificando(false);
          return;
        }

        const response = await api.get('/conductor/verificar-aceptaciones');
        const data = response.data.data;

        setTycAceptados(data.tyc_aceptados);
        setPoliticaPrivacidadAceptada(data.politica_privacidad_aceptada);

        // Verificar si necesita mostrar modales
        if (!data.tyc_aceptados) {
          setMostrarModalTerminos(true);
        } else if (!data.politica_privacidad_aceptada) {
          setMostrarModalPolitica(true);
        }

        setVerificando(false);
      } catch (error) {
        console.error('Error al verificar aceptaciones:', error);
        setVerificando(false);
      }
    };

    verificarAceptaciones();
  }, []);

  const aceptarTerminos = async () => {
    try {
      await api.post('/conductor/aceptar-terminos', {
        tyc_version: TYC_VERSION
      });
      setTycAceptados(true);
      setMostrarModalTerminos(false);

      // Mostrar modal de política de privacidad si no ha sido aceptada
      if (!politicaPrivacidadAceptada) {
        setTimeout(() => {
          setMostrarModalPolitica(true);
        }, 500);
      }

      return true;
    } catch (error) {
      console.error('Error al aceptar términos:', error);
      return false;
    }
  };

  const aceptarPoliticaPrivacidad = async () => {
    try {
      await api.post('/conductor/aceptar-politica-privacidad');
      setPoliticaPrivacidadAceptada(true);
      setMostrarModalPolitica(false);
      return true;
    } catch (error) {
      console.error('Error al aceptar política de privacidad:', error);
      return false;
    }
  };

  const contextValue = useMemo(
    () => ({
      tycAceptados,
      politicaPrivacidadAceptada,
      verificando,
      mostrarModalTerminos,
      mostrarModalPolitica,
      setMostrarModalTerminos,
      setMostrarModalPolitica,
      aceptarTerminos,
      aceptarPoliticaPrivacidad,
      TYC_VERSION,
    }),
    [tycAceptados, politicaPrivacidadAceptada, verificando, mostrarModalTerminos, mostrarModalPolitica]
  );

  return (
    <TermsContext.Provider value={contextValue}>
      {children}
    </TermsContext.Provider>
  );
};

TermsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
